// Light/dark theme control. The palette itself lives in CSS
// (styles/colors_and_type.css): `prefers-color-scheme` applies by default,
// and a `data-theme` attribute on <html> overrides it. This module persists
// an explicit user choice to localStorage and reflects it onto <html>; with
// no stored choice the attribute is absent and the system preference wins.

const STORAGE_KEY = "eyes-theme";

export type Theme = "light" | "dark";

function storedTheme(): Theme | null {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === "light" || v === "dark" ? v : null;
    } catch {
        // localStorage can throw in private mode / sandboxed iframes.
        return null;
    }
}

function systemPrefersDark(): boolean {
    return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
    );
}

/** The theme actually rendering right now: the stored choice if any,
 *  otherwise whatever the OS/browser prefers. */
export function effectiveTheme(): Theme {
    return storedTheme() ?? (systemPrefersDark() ? "dark" : "light");
}

/** Reflect the stored preference onto <html>. Called once at startup so an
 *  explicit choice survives reloads. With no stored choice we clear the
 *  attribute and let `prefers-color-scheme` take over. */
export function applyStoredTheme(): void {
    const t = storedTheme();
    if (t) document.documentElement.setAttribute("data-theme", t);
    else document.documentElement.removeAttribute("data-theme");
}

/** Flip to the opposite of whatever is showing now, persist the choice, and
 *  apply it to <html>. Returns the newly-active theme. */
export function toggleTheme(): Theme {
    const next: Theme = effectiveTheme() === "dark" ? "light" : "dark";
    try {
        localStorage.setItem(STORAGE_KEY, next);
    } catch {
        // Persistence is best-effort; the in-memory attribute still applies.
    }
    document.documentElement.setAttribute("data-theme", next);
    return next;
}

/** Hand the theme entirely to the OS: drop any manual override so
 *  `prefers-color-scheme` alone decides. Used inside the desktop apps, where
 *  the platform already exposes a dark-mode setting and we hide the toggle.
 *  Also clears any stored choice so a stale explicit pick (e.g. carried over
 *  from before this shipped) can't pin the app against the OS. */
export function followSystemTheme(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing stored / storage blocked — the attribute clear below still wins.
    }
    document.documentElement.removeAttribute("data-theme");
}

/** Call `onChange` whenever the OS light/dark preference flips. The CSS
 *  repaints itself off `prefers-color-scheme`, but colors read in JS (the
 *  cursor/presence canvas layer) only refresh on a re-render — so callers pass
 *  a re-render. No-op where `matchMedia` is unavailable. */
export function watchSystemTheme(onChange: () => void): void {
    if (typeof window.matchMedia !== "function") return;
    window
        .matchMedia("(prefers-color-scheme: dark)")
        .addEventListener("change", onChange);
}
